import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Besprechung, Keyword, Swimlane } from '../../../../generated-api';
import { MarkdownComponent } from 'ngx-markdown';
import { SubSink } from 'subsink';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslateService } from '@ngx-translate/core';
import * as Constants from '../../../../core/helpers/constants/constants';
import { BesprechungStoreService } from '../../stores/besprechung-store.service';
import {
  LegendDialogComponent,
  LegendDialogModes,
} from '../../../../shared/components/legend-dialog/legend-dialog.component';
import { MatDialog } from '@angular/material/dialog';

import titleReplacements from '../../../../../assets/config/title-replacements.json';
import { LinkHandlerService } from '../../../../core/services/markdown-link-handler.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-besprechung-view',
  templateUrl: './besprechung-view.component.html',
  styleUrls: ['./besprechung-view.component.scss'],
  standalone: false,
})
export class BesprechungViewComponent implements OnChanges, OnDestroy {
  @Input() details: Besprechung;
  @Input() loading = false;
  chips = [];
  picture = null;
  swimlanes: Swimlane;
  swimlaneCopyright = '-';
  swimlaneCopyrightTooltip = undefined;
  srcSequence = [
    'kino',
    'netflix',
    'amazon',
    'disney_plus',
    'kika',
    'toggo',
    'ARD Mediathek',
    'ZDF Mediathek',
    'RTLPLUS',
    'Joyn',
    'joyn',
    'filmfriend',
    'youtube',
    'youtubekids',
    'tv',
    'KKA',
    'RTLS',
    'Disney Channel Free TV',
    'NICK',
    'RTL',
    'RTL2',
    'PRO7',
    'SAT1',
    'K1',
    'ARD',
    'ZDF',
  ];

  subs = new SubSink();

  keywordsArray: Keyword[] = [];


  get filledQualities(): any[] {
    if (this.details && this.details.rating) {
      const qualities = this.details.rating.qualities;
      if (qualities) {
        return Object.keys(qualities).filter((value) => qualities[value]);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  @ViewChild('mkBox1') markdown4: MarkdownComponent;
  seoScript: HTMLScriptElement;
  seoScriptInitialized = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private translateService: TranslateService,
    private store: BesprechungStoreService,
    private matDIalog: MatDialog,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: any,
    public lh: LinkHandlerService
  ) {
    this.seoScript = this.renderer.createElement('script');
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.picture = null;
    if (changes.details && changes.details.currentValue) {

      this.chips = this.details.srclist
        .filter((v) => v.src !== 'kino')
        .sort(
          (a, b) =>
            this.srcSequence.indexOf(a.src) - this.srcSequence.indexOf(b.src)
        );

      this.picture = this.details.image
        ? this.details.image
        : Constants.PLACEHOLDER_IMAGE_WITH_PATH;
      this.keywordsArray = Object.keys(this.details.keywords).map((value) => ({
        id: Number(value),
        name: this.details.keywords[value],
      }));
      this.swimlanes = {
        title: this.translateService.instant('besprechung.swimlaneTitle'),
        items: this.details.similaritems?.slice(0, 10) ?? [],
        swimlaneclass: 'default',
      };

      const copyrights = new Array<string>();
      copyrights.push(this.details.imagec);

      if (this.details.similaritems != null) {
        for (let i = 0; i < this.details.similaritems.length; i++) {
          const item = this.details.similaritems[i];
          if (copyrights.indexOf(item.imagec) < 0) {
            copyrights.push(item.imagec);
          }
        }
      }

      this.swimlaneCopyright = Array.from(copyrights.values())
        .slice(0, 6)
        .join(', ');
      if (copyrights.length > 6) {
        this.swimlaneCopyright += '...';
        this.swimlaneCopyrightTooltip = Array.from(copyrights.values()).join(
          ', '
        );
      }

      // json-ld ala https://javascript.plainenglish.io/how-to-use-json-ld-for-advanced-seo-in-angular-63528c98bb91
      if (document) {
        let script;
        let shouldAppend = false;
        if (document.head.getElementsByClassName('structured-data').length) {
          script = document.head.getElementsByClassName('structured-data')[0];
        } else {
          script = document.createElement('script');
          shouldAppend = true;
        }
        script.setAttribute('class', 'structured-data');
        script.type = 'application/ld+json';

        // TODO: don't use MD just plain text in content & reviewBody
        const content = this.details.content ?? this.details.contentMD;
        let reviewBody = content + '\n\nFLIMMO meint: ' + this.details.paed;
        if (this.details.warnung) reviewBody += '\n\nAchtung: ' + this.details.warnung;
        if (this.details.rating.qualities?.humor) reviewBody += '\n\nHumor: ' + this.details.rating.qualities.humor;
        if (this.details.rating.qualities?.wissen) reviewBody += '\n\nWissen: ' + this.details.rating.qualities.wissen;
        if (this.details.rating.qualities?.machart) reviewBody += '\n\nMachart: ' + this.details.rating.qualities.machart;
        if (this.details.rating.qualities?.vorbild) reviewBody += '\n\nVorbild: ' + this.details.rating.qualities.vorbild;
        if (this.details.rating.qualities?.spannung) reviewBody += '\n\nSpannung: ' + this.details.rating.qualities.spannung;
        if (this.details.rating.qualities?.botschaft) reviewBody += '\n\nBotschaft: ' + this.details.rating.qualities.botschaft;
        if (this.details.box1) reviewBody += '\n\n' + this.details.box1;
        if (this.details.box2) reviewBody += '\n\n' + this.details.box2;
        if (this.details.box3) reviewBody += '\n\n' + this.details.box3;

        const schemaOrg = {
          '@context': 'http://schema.org',
          '@type': 'CriticReview',
          publisher: {
            '@type': 'Organization',
            sameAs: '/redtext/101225/Herausgeber',
            name: 'Programmberatung für Eltern e.V.',
          },
          author: {
            '@type': 'Organization',
            sameAs: '/redtext/101237/Redaktion',
            name: 'JFF – Institut für Medienpädagogik in Forschung und Praxis',
          },
          'inLanguage': 'German',
          'url': '/besprechung/' + this.details.id + '/' + this.prettifyUrl(this.details.title),
          'datePublished': this.details.publishedDate ?? '2025-04-23',
          'headline': this.details.title,
          'description': content.substr(0, 198) + '..',
          'reviewBody': reviewBody,
          'itemReviewed': {
            '@type': (this.details.genre == 'Spielfilm' ||  this.details.genre == 'Animationsfilm') ? 'Movie' : 'TVSeries',
            'sameAs': (this.details.imdb) ? 'https://www.imdb.com/title/' + this.details.imdb : '/besprechung/' + this.details.id + '/' + this.prettifyUrl(this.details.title),
            'name': this.details.title,
            'image': this.details.image,
          },
          // contentRating ist f USK
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': (this.details.rating.id == 4) ? 'FLIMMO findet\'s gut! (ab '+ this.details.altersfreigabe +')' : (this.details.rating.id == 3) ? 'Grün (ab '+ this.details.altersfreigabe +')' : (this.details.rating.id == 2) ? 'Gelb (aus pädagogischer Sicht problematisch)' : 'Rot (nicht für Kinder geeignet)',
          },
        };

        if (this.details.altersfreigabe) {
          schemaOrg.itemReviewed['audience'] = {
            '@type': 'Audience',
            'suggestedMinAge': this.details.altersfreigabe
          }
        }

        script.text = JSON.stringify(schemaOrg);

        if (shouldAppend) {
          document.head.appendChild(script);
        }
      }
    }
  }

  prettifyUrl(title: string): string {
    if (!title) title = '';
    let prettyString = title.toLocaleLowerCase();
    titleReplacements.forEach((val) => {
      prettyString = prettyString.split(val.from).join(val.to);
    });
    return prettyString;
  }

  onImgError(): void {
    this.picture = Constants.PLACEHOLDER_IMAGE_WITH_PATH;
  }

  mkReady(element): void {
    this.lh.wireInsertedLinks(element.element);
  }

  showApproved(): boolean {
    return this.details && this.details.rating.id === 4;
  }

  ngOnDestroy(): void {
    this.store.besprechung = null;
  }

  toLegend(): void {
    this.matDIalog.open(LegendDialogComponent, {
      width: '400px',
      data: { mode: LegendDialogModes.besprechung },
    });
  }
}
